/* import __COLOCATED_TEMPLATE__ from './score-distribution.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { use } from 'ember-resources/util/function-resource';
import type IntlService from 'ember-intl/services/intl';
import type { CxBenchmarkGroup, CxScoreDistribution } from './types';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';

interface ScoreSegment {
  label: string;
  tooltipText: string;
  colorClass: string;
  yourWidth: number;
  peerWidth: number;
}

interface Signature {
  Args: {
    benchmarkGroup: CxBenchmarkGroup | undefined;
    reportState?: ReportState;
  };
}

// Score keys ordered highest to lowest for display (5→1), matching the CX score section bar
const SCORE_KEYS = ['5', '4', '3', '2', '1'] as const;

const SCORE_LABELS: Record<string, string> = {
  '5': 'components.reporting.bespoke.fin-performance.cx-benchmark-panel.score-distribution.score-5',
  '4': 'components.reporting.bespoke.fin-performance.cx-benchmark-panel.score-distribution.score-4',
  '3': 'components.reporting.bespoke.fin-performance.cx-benchmark-panel.score-distribution.score-3',
  '2': 'components.reporting.bespoke.fin-performance.cx-benchmark-panel.score-distribution.score-2',
  '1': 'components.reporting.bespoke.fin-performance.cx-benchmark-panel.score-distribution.score-1',
};

const SCORE_DIST_KEYS: Record<string, keyof CxScoreDistribution> = {
  '5': 'score_5',
  '4': 'score_4',
  '3': 'score_3',
  '2': 'score_2',
  '1': 'score_1',
};

const SCORE_TOOLTIP_KEYS: Record<string, string> = {
  '5': 'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-score-5',
  '4': 'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-score-4',
  '3': 'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-score-3',
  '2': 'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-score-2',
  '1': 'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-score-1',
};

const SCORE_COLORS: Record<string, string> = {
  '1': 'bg-vis-orange-30',
  '2': 'bg-vis-orange-50',
  '3': 'bg-vis-orange-70',
  '4': 'bg-vis-violet-50',
  '5': 'bg-vis-violet-20',
};

function toPercentages(getValue: (key: string) => number): Record<string, number> {
  let total = 0;
  for (let key of SCORE_KEYS) {
    total += getValue(key);
  }
  let widths: Record<string, number> = {};
  for (let key of SCORE_KEYS) {
    widths[key] = total > 0 ? (getValue(key) / total) * 100 : 0;
  }
  return widths;
}

export default class ScoreDistribution extends Component<Signature> {
  @service declare intl: IntlService;
  @tracked hoveredSegmentLabel: string | undefined;

  @action
  onSegmentHovered(segment: ScoreSegment) {
    this.hoveredSegmentLabel = segment.label;
  }

  @action
  onSegmentExited() {
    this.hoveredSegmentLabel = undefined;
  }

  @action
  shouldDimSegment(segment: ScoreSegment): boolean {
    if (!this.hoveredSegmentLabel) {
      return false;
    }
    return this.hoveredSegmentLabel !== segment.label;
  }

  @action
  formatPercentage(width: number): string {
    return `${Math.round(width)}%`;
  }

  @use dataResource = staticDataResource(this, {
    serializedChart: () => this.chartDefinition,
    reportState: () => this.args.reportState as ReportState,
  });

  get chartDefinition() {
    return {
      visualization_type: 'horizontal-bar',
      view_by: 'ai_generated_metrics.csat',
      stacked: true,
      chart_series: [
        {
          metric_id:
            'conversation.ai_generated_metrics.csat.count.first_user_conversation_part_created_at',
          aggregation: 'count',
          show_data_labels: false,
          filters: {
            type: 'and',
            filters: [
              {
                type: 'category',
                data: {
                  property: 'agent_types_involved_grouping',
                  attribute: 'conversation.agent_types_involved_grouping',
                  values: ['ai-agent'],
                },
              },
            ],
          },
        },
      ],
    };
  }

  get hasReportState(): boolean {
    return this.args.reportState !== undefined;
  }

  get isLoading(): boolean {
    if (!this.hasReportState) {
      return false;
    }
    return this.dataResource?.isLoading ?? true;
  }

  get hasYourData(): boolean {
    if (!this.hasReportState || this.isLoading) {
      return false;
    }
    let flatData = this.dataResource?.flatCombinedData ?? {};
    return SCORE_KEYS.some((key) => ((flatData[key]?.[0] as number) ?? 0) > 0);
  }

  get yourScoreWidths(): Record<string, number> {
    let flatData = this.dataResource?.flatCombinedData ?? {};
    return toPercentages((key) => (flatData[key]?.[0] as number) ?? 0);
  }

  get peerScoreWidths(): Record<string, number> {
    let dist = this.args.benchmarkGroup?.score_distribution;
    return toPercentages((key) => dist?.[SCORE_DIST_KEYS[key]] ?? 0);
  }

  get segments(): ScoreSegment[] {
    let yourWidths = this.yourScoreWidths;
    let peerWidths = this.peerScoreWidths;
    return SCORE_KEYS.map((key) => ({
      label: this.intl.t(SCORE_LABELS[key]),
      tooltipText: this.intl.t(SCORE_TOOLTIP_KEYS[key]),
      colorClass: SCORE_COLORS[key],
      yourWidth: yourWidths[key],
      peerWidth: peerWidths[key],
    }));
  }

  get legendItems(): { label: string; colorClass: string }[] {
    return this.segments.map((segment) => ({
      label: segment.label,
      colorClass: segment.colorClass,
    }));
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::FinPerformance::CxBenchmarkPanel::ScoreDistribution': typeof ScoreDistribution;
    'reporting/bespoke/fin-performance/cx-benchmark-panel/score-distribution': typeof ScoreDistribution;
  }
}
